/**
 * Беседа через переводчика.
 *
 * Телефон лежит на столе между заказчиком и поставщиком. Экран поделён
 * пополам: нижняя половина — заказчику, верхняя повёрнута к поставщику.
 * Каждый читает на своей половине перевод слов собеседника, на своём языке.
 * Новая фраза появляется у края, ближнего к читающему, старые уходят
 * к середине.
 *
 * Кто говорит, не определяем: каждая линия перевода сама молчит, когда
 * слышит речь на своём же языке (см. lib/interpret.ts).
 *
 * Беседа сохраняется сама, по ходу разговора: реплики пишутся в базу
 * телефона, синхронизация отвозит их на сервер (см. flushTalks в sync.ts).
 * Перед началом спрашиваем стенд — его можно пропустить и привязать потом.
 */
import { useEffect, useRef, useState } from 'react';
import {
  db, enqueue, setting, setSetting,
  type LocalExhibition, type LocalSupplier, type LocalTalk, type TalkLine,
} from '../db';
import { syncNow } from '../sync';
import { keepAwake } from '../lib/media';
import {
  LANGS, langOf, openMic, joinDelta, InterpretLine, type LineState,
} from '../lib/interpret';
import { StandPick } from './Talks';
import { ulid } from '@core';

type Side = 'mine' | 'theirs';
type Phase = 'idle' | 'starting' | 'live' | 'paused';

/**
 * Когда начинать новую строку. Переводчик замолкает и посреди фразы — ждёт,
 * чем она кончится, — поэтому одной паузы мало: новая строка начинается,
 * если прежняя договорила фразу до точки, или если молчали совсем долго.
 */
const NEW_LINE_AFTER_MS = 1200;
const NEW_LINE_ANYWAY_MS = 4000;
const SENTENCE_END = /[.!?…。！？]["»”’)\]]*\s*$/;
/** Сколько строк держать на половине: беседа читается, а не листается. */
const KEEP_LINES = 40;
/** Переподключений подряд, после которых перестаём пробовать и говорим человеку. */
const MAX_RETRIES = 3;

export default function Interpret({ exhibition, supplierId, onBack }: {
  exhibition: LocalExhibition | null;
  /** Беседу открыли из карточки стенда — спрашивать, с кем она, незачем. */
  supplierId?: string;
  onBack: () => void;
}) {
  const [lang, setLang] = useState<Record<Side, string>>({ mine: 'ru', theirs: 'en' });
  const [phase, setPhase] = useState<Phase>('idle');
  const [feed, setFeed] = useState<Record<Side, TalkLine[]>>({ mine: [], theirs: [] });
  const [state, setState] = useState<Record<Side, LineState | null>>({ mine: null, theirs: null });
  const [problem, setProblem] = useState('');
  const [stand, setStand] = useState<LocalSupplier | null>(null);
  // Выбор стенда: перед началом — если стенд не известен заранее, и по метке в полосе.
  const [picking, setPicking] = useState(!supplierId);

  const langRef = useRef(lang);
  langRef.current = lang;
  const standRef = useRef<LocalSupplier | null>(null);
  const running = useRef(false);
  const mic = useRef<MediaStream | null>(null);
  const lines = useRef<Partial<Record<Side, InterpretLine>>>({});
  const lastAt = useRef<Record<Side, number>>({ mine: 0, theirs: 0 });
  const retries = useRef<Record<Side, number>>({ mine: 0, theirs: 0 });
  const release = useRef<() => void>(() => {});
  // Номер запуска: остановка его меняет, и запуск, застрявший на вопросе
  // про микрофон, по возвращении видит, что его уже отменили.
  const gen = useRef(0);

  // Запись беседы. Заводится на первой реплике: открыли и закрыли, ничего
  // не сказав, — пустой беседы в списке не будет.
  const talk = useRef<LocalTalk | null>(null);
  const openLineOf = useRef<Record<Side, TalkLine | null>>({ mine: null, theirs: null });
  const saveTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    void (async () => {
      const mine = await setting<string>('talk.mine', 'ru');
      const theirs = await setting<string>('talk.theirs', 'en');
      setLang({ mine, theirs: theirs === mine ? (mine === 'en' ? 'zh' : 'en') : theirs });
      if (supplierId) {
        const s = (await db.suppliers.get(supplierId)) ?? null;
        standRef.current = s;
        setStand(s);
      }
    })();
    return () => { stopAll(); save(true); void syncNow(); };
  }, []);

  /** Записать беседу в базу телефона. Часто — с задержкой, по делу — сразу. */
  function save(now = false) {
    window.clearTimeout(saveTimer.current);
    const write = () => {
      const t = talk.current;
      if (t) void db.talks.put({ ...t, lines: t.lines.map((l) => ({ ...l })), dirty: true });
    };
    if (now) write();
    else saveTimer.current = window.setTimeout(write, 1200);
  }

  function push(side: Side, delta: string, code: string) {
    const now = Date.now();
    const quiet = now - lastAt.current[side];
    lastAt.current[side] = now;
    retries.current[side] = 0;

    const at = new Date(now).toISOString();
    if (!talk.current) {
      talk.current = {
        id: ulid(), exhibitionId: exhibition?.id, supplierId: standRef.current?.id,
        mineLang: langRef.current.mine, theirsLang: langRef.current.theirs,
        lines: [], startedAt: at, tzOffset: -new Date().getTimezoneOffset(),
      };
    }
    const t = talk.current;
    const last = openLineOf.current[side];
    const fresh = !last || quiet > NEW_LINE_ANYWAY_MS
      || (quiet > NEW_LINE_AFTER_MS && SENTENCE_END.test(last.text));
    if (fresh) {
      // Нижняя половина показывает слова поставщика, верхняя — заказчика.
      const line: TalkLine = {
        n: t.lines.length + 1, at, who: side === 'mine' ? 'them' : 'me', lang: code,
        text: joinDelta('', delta),
      };
      t.lines.push(line);
      openLineOf.current[side] = line;
    } else {
      last.text = joinDelta(last.text, delta);
    }
    t.endedAt = at;
    setFeed({
      mine: t.lines.filter((l) => l.who === 'them').slice(-KEEP_LINES),
      theirs: t.lines.filter((l) => l.who === 'me').slice(-KEEP_LINES),
    });
    save();
  }

  function openLine(side: Side) {
    lines.current[side]?.stop();
    const track = mic.current?.getAudioTracks()[0];
    if (!track) return;
    const code = langRef.current[side];
    const line = new InterpretLine(code, track, {
      onText: (d) => push(side, d, code),
      onState: (s) => {
        setState((st) => ({ ...st, [side]: s }));
        if (s === 'live') retries.current[side] = 0;
        if (s === 'lost') retry(side);
      },
      onFatal: (message) => {
        setProblem(message);
        pause();
      },
    });
    lines.current[side] = line;
    line.start().catch((e) => {
      console.warn('interpret start', side, e);
      retry(side);
    });
  }

  function retry(side: Side) {
    if (!running.current) return;
    if (retries.current[side] >= MAX_RETRIES) {
      setProblem(navigator.onLine
        ? 'Переводчик не отвечает. Нажмите «Продолжить», чтобы попробовать ещё раз.'
        : 'Пропала сеть. Перевод идёт только онлайн — нажмите «Продолжить», когда связь вернётся.');
      pause();
      return;
    }
    retries.current[side] += 1;
    const wait = 1500 * retries.current[side];
    window.setTimeout(() => { if (running.current) openLine(side); }, wait);
  }

  async function start() {
    setProblem('');
    if (!navigator.onLine) {
      setProblem('Нет сети. Перевод идёт только онлайн.');
      return;
    }
    const my = gen.current;
    setPhase('starting');
    let stream: MediaStream;
    try {
      stream = await openMic();
    } catch {
      setProblem('Нет доступа к микрофону. Разрешите его в настройках браузера для gemsnote.app.');
      setPhase('idle');
      return;
    }
    const unlock = await keepAwake();
    if (gen.current !== my) {
      // Экран закрыли, пока браузер спрашивал про микрофон.
      stream.getTracks().forEach((t) => t.stop());
      unlock();
      return;
    }
    mic.current = stream;
    release.current = unlock;
    running.current = true;
    retries.current = { mine: 0, theirs: 0 };
    openLine('mine');
    openLine('theirs');
    setPhase('live');
  }

  /** Остановить всё: пауза бережёт деньги — модель считает минуты, пока слушает. */
  function stopAll() {
    gen.current += 1;
    running.current = false;
    lines.current.mine?.stop();
    lines.current.theirs?.stop();
    lines.current = {};
    mic.current?.getTracks().forEach((t) => t.stop());
    mic.current = null;
    release.current();
    release.current = () => {};
    setState({ mine: null, theirs: null });
  }

  function pause() {
    stopAll();
    save(true);
    void syncNow();
    setPhase('paused');
  }

  function pickLang(side: Side, code: string) {
    const next = { ...langRef.current, [side]: code };
    langRef.current = next;
    setLang(next);
    void setSetting(side === 'mine' ? 'talk.mine' : 'talk.theirs', code);
    // Язык сменили посреди беседы — линия переподключается на новый.
    if (running.current) openLine(side);
  }

  /**
   * Стенд выбран или сменён. Первый выбор перед беседой сразу её начинает:
   * человек у стенда, и лишнее нажатие ему ни к чему.
   */
  function chooseStand(s: LocalSupplier | null) {
    const first = phase === 'idle' && !talk.current;
    standRef.current = s;
    setStand(s);
    setPicking(false);
    const t = talk.current;
    if (t) {
      t.supplierId = s?.id;
      save(true);
      // Беседа могла уже уйти на сервер снимком — привязку меняет своя операция.
      void enqueue('talk.link', { id: t.id, supplierId: s?.id ?? null }, ulid());
    }
    if (first) void start();
  }

  if (picking) {
    return (
      <div className="talk">
        <StandPick
          exhibitionId={exhibition?.id}
          title="С кем разговор?"
          skipLabel={stand ? 'Без стенда' : 'Пропустить — стенда ещё нет'}
          onPick={(s) => chooseStand(s)}
          onSkip={() => chooseStand(null)}
          onCancel={() => (phase === 'idle' && !talk.current ? onBack() : setPicking(false))}
        />
      </div>
    );
  }

  const mine = langOf(lang.mine);
  const theirs = langOf(lang.theirs);
  const status =
    phase === 'idle' ? 'Положите телефон между собой экраном вверх'
    : phase === 'starting' ? 'Подключаю переводчик…'
    : phase === 'paused' ? 'Пауза'
    : state.mine === 'live' && state.theirs === 'live' ? `Слушаю · ${mine.ru} ⇄ ${theirs.ru}`
    : 'Подключаю переводчик…';

  return (
    <div className="talk">
      <Half
        flipped lang={lang.theirs} other={lang.mine} lines={feed.theirs}
        waiting={phase !== 'live'} onPick={(c) => pickLang('theirs', c)}
      />

      <div className="talk-mid">
        <button className="talk-x" onClick={onBack} aria-label="Закрыть">✕</button>
        <div className="talk-status">
          <span className="talk-dot" data-on={phase === 'live' && state.mine === 'live' && state.theirs === 'live' ? '1' : '0'} />
          <span className="talk-status-text">
            <span>{problem || status}</span>
            <button className="talk-stand" onClick={() => setPicking(true)}>
              {stand?.companyName ?? 'Без стенда'} <span aria-hidden>▾</span>
            </button>
          </span>
        </div>
        {phase === 'live' ? (
          <button className="btn btn-secondary btn-sm talk-go" onClick={pause}>Пауза</button>
        ) : (
          <button className="btn btn-primary btn-sm talk-go" disabled={phase === 'starting'} onClick={() => void start()}>
            {phase === 'paused' ? 'Продолжить' : 'Начать'}
          </button>
        )}
      </div>

      <Half
        lang={lang.mine} other={lang.theirs} lines={feed.mine}
        waiting={phase !== 'live'} onPick={(c) => pickLang('mine', c)}
      />
    </div>
  );
}

/**
 * Половина одного читающего. Верхняя повёрнута целиком, вместе с выбором
 * языка: поставщик сам может ткнуть в свой язык, не выворачивая голову.
 */
function Half({ flipped, lang, other, lines, waiting, onPick }: {
  flipped?: boolean;
  lang: string;
  /** Язык собеседника — выбрать его и здесь нельзя: переводить было бы не с чего. */
  other: string;
  lines: TalkLine[];
  waiting: boolean;
  onPick: (code: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const me = langOf(lang);
  const newestFirst = [...lines].reverse();

  return (
    <section className={'talk-half' + (flipped ? ' flip' : '')} lang={lang}>
      <header className="talk-head">
        <button className="talk-lang" onClick={() => setPicking(true)}>
          {me.name} <span aria-hidden>▾</span>
        </button>
      </header>

      {/* column-reverse: первая в разметке — самая новая, и она у ближнего края. */}
      <div className="talk-feed">
        {newestFirst.length === 0 ? (
          <p className="talk-hint">{waiting ? `${me.start}\n${me.hint}` : me.hint}</p>
        ) : newestFirst.map((l, i) => (
          <p key={l.n} className={'talk-line' + (i === 0 ? ' now' : i > 2 ? ' old' : '')}>{l.text}</p>
        ))}
      </div>

      {picking && (
        <div className="talk-pick">
          <div className="talk-pick-grid">
            {LANGS.map((l) => (
              <button
                key={l.code}
                className="talk-pick-item" data-picked={l.code === lang ? '1' : '0'}
                disabled={l.code === other}
                onClick={() => { setPicking(false); if (l.code !== lang) onPick(l.code); }}
              >
                {l.name}
              </button>
            ))}
          </div>
          <button className="talk-pick-close" onClick={() => setPicking(false)} aria-label="Закрыть">✕</button>
        </div>
      )}
    </section>
  );
}
